import React, { useState } from "react";
import { Trash2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";
import { base44 } from "@/api/base44Client";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function DeleteAccountSection() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const isAdmin = user?.role === "admin" || user?.role === "owner";

  const requestDelete = async () => {
    setDeleting(true);
    try {
      if (isAdmin) {
        const body =
          `Account deletion request received.\n\n` +
          `User: ${user?.full_name || "—"}\n` +
          `Email: ${user?.email || "—"}\n` +
          `Date: ${new Date().toISOString()}\n\n` +
          `This request was submitted from the DrillLog Pro app. Please process the permanent deletion of this account and associated data.`;
        await base44.integrations.Core.SendEmail({
          to: user?.email,
          subject: "Account deletion request received",
          body,
        });
      } else {
        await base44.functions.invoke("requestAccountDeletion", {});
      }
    } catch (e) {
      console.error(e);
    } finally {
      setDeleting(false);
      setOpen(false);
      logout();
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-red-200 p-6 dark:bg-slate-900 dark:border-red-900">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 space-y-2">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2 dark:text-slate-100">
            <Trash2 className="w-4 h-4 text-red-600" /> Delete Account
          </h3>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Submit a request to permanently delete your account and associated data.
            This action cannot be undone.
          </p>
        </div>
        <Button
          variant="destructive"
          className="rounded-xl gap-2 shrink-0"
          onClick={() => setOpen(true)}
        >
          <Trash2 className="w-4 h-4" /> Delete Account
        </Button>
      </div>

      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete account?</AlertDialogTitle>
            <AlertDialogDescription>
              {isAdmin
                ? "This will submit a request to permanently delete your DrillLog Pro account and data. You will be signed out after the request is sent. This action cannot be undone."
                : "This will submit a deletion request to admin accounts for approval. You will be signed out after the request is sent. If not approved within 24 hours, the request will expire."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={deleting}
              onClick={(e) => {
                e.preventDefault();
                requestDelete();
              }}
            >
              {deleting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
              Request Deletion
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}